/**
 * This file contains utility functions that are used across the app
 * @module util
 */

import * as THREE from 'three';
import React from 'react';


/** 
 * The React Context AppContext contains the collection meta data.
 * These values will be overwritten with the app state
 * @type {Object}
 * @see module:App
 */
export const AppContext = React.createContext({
  data: []
});


/**
 * loads a THREE.Texture as a Promise
 * @param {string} url URL of the texture file
 * @return {Object} a Promise resolving with a THREE.Texture when the texture is loaded
 */
export function loadTexture(url) {
    return new Promise(resolve => {
        new THREE.TextureLoader().load(url, resolve, undefined, err => console.error('TextureLoader Error', err));
    });
}


/**
 * Maps a input value ranging 0-1 to a given range
 * @param {number} value the input value
 * @param {number} out_min lower boundry
 * @param {number} out_max upper boundry
 * @return {number} the mapped normal
 */
export function mapNormal(value, out_min, out_max) {
    const in_min = 0;
    const in_max = 1;
    return (value - in_min) * (out_max - out_min) / (in_max - in_min) + out_min;
}


/**
 * Generates a random number within a given range
 * @param {number} min lower boundry
 * @param {number} max upper boundry
 * @param {boolean} [round] true, if result should be rounded to an integer
 * @return {number} a random number
 */
export function randMinMax(min, max, round) {
	var val = min + (Math.random() * (max - min));
	if(round) val = Math.round(val);
	return val;
};


/**
 * A sorting function comparing Items by year
 * @see module:components/Item
 * @param {Object} a the first Item for comparison
 * @param {Object} b the second Item for comparison
 * @return comparison result as -1, 0 or 1
 */
export function sortByYear(a, b) {

    if(!a.props.year && !b.props.year) {
      return 0;
    }
    if(a.props.year && !b.props.year) {
      return -1;
    }
    if(!a.props.year && b.props.year) {
      return 1;
    }

    if(a.props.year > b.props.year) {
      return 1;
    } else {
      return -1;
    }
}


/**
 * Creates a text label to be used in 3D-Space
 * @param {string} text label text
 * @param {number} sizeX height factor of the texture (default: 2) 
 * @param {number} sizeY width factor of the texture (default: 2)
 * @param {boolean} [useFiraSans] true, if the font FiraSans should be used
 * @return {Object} the text label as a THREE.Mesh
 */
export function createTextLabel(text, sizeX, sizeY, useFiraSans) {
    const canvas = document.createElement('canvas'),
    ctx = canvas.getContext('2d');

    sizeX = sizeX ? sizeX : 2;
    sizeY = sizeY ? sizeY : 2;

    canvas.width = 128*sizeX;
    canvas.height = 128*sizeY;

    ctx.font = useFiraSans ? '64px FiraSans' : 'bold 64px Merriweather';
    ctx.fillStyle = 'white';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.Texture(canvas);
    const geometry = new THREE.PlaneGeometry(sizeX, sizeY);
    const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, alphaTest: 0.1 });
    const label = new THREE.Mesh(geometry, material);
    texture.needsUpdate = true;

    return label;
}